import { type FormEvent, useEffect, useState } from "react";

import { api, ApiError } from "@/api/client";
import type {
  TotpBackupCodesResponse,
  TotpSetupResponse,
  TotpStatusResponse,
} from "@/api/types";
import { BackupCodesDisplay } from "@/components/BackupCodesDisplay";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

type SetupStep = "idle" | "show-codes" | "verify" | "done";

/**
 * Pantalla `/account/security`. Dos modos según el estado del usuario:
 *
 * - No enrolado: ofrece "Activar 2FA". Iniciá el setup → muestra QR + 10
 *   backup codes → pide el código del autenticador → confirma → vuelve al
 *   modo enrolado.
 *
 * - Enrolado: muestra estado (cuándo se activó, último uso, backups
 *   restantes). Botones: "Regenerar códigos de respaldo" y "Desactivar 2FA".
 *   Ambos piden código TOTP válido.
 *
 * CLI 43 — branding AROC (AP-11): nunca menciona apps comerciales por
 * nombre, solo "tu app TOTP" / "tu autenticador".
 */
export function SecurityPage() {
  const [status, setStatus] = useState<TotpStatusResponse | null>(null);
  const [setupState, setSetupState] = useState<SetupStep>("idle");
  const [setupData, setSetupData] = useState<TotpSetupResponse | null>(null);
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [regenerated, setRegenerated] = useState<string[] | null>(null);

  async function loadStatus() {
    try {
      const s = await api<TotpStatusResponse>("/api/v1/admin/totp/status");
      setStatus(s);
    } catch (err) {
      setError("No se pudo cargar el estado del segundo factor.");
    }
  }

  useEffect(() => {
    void loadStatus();
  }, []);

  async function startSetup() {
    setError(null);
    setSubmitting(true);
    try {
      const data = await api<TotpSetupResponse>("/api/v1/admin/totp/setup", {
        method: "POST",
      });
      setSetupData(data);
      setSetupState("show-codes");
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        setError("El segundo factor ya está activo.");
        await loadStatus();
      } else {
        setError("No se pudo iniciar el enrolamiento.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  async function confirmEnable(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    if (!/^\d{6}$/.test(code)) {
      setError("Ingresa el código de 6 dígitos.");
      return;
    }
    setSubmitting(true);
    try {
      await api<void>("/api/v1/admin/totp/enable", { method: "POST", body: { code } });
      setSetupState("done");
      setCode("");
      await loadStatus();
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setError("Código inválido. Prueba con el código actual del autenticador.");
      } else {
        setError("No se pudo confirmar el enrolamiento.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  async function disable(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    if (!/^\d{6}$/.test(code)) {
      setError("Ingresa el código de 6 dígitos para confirmar.");
      return;
    }
    if (!window.confirm("¿Seguro que quieres desactivar la autenticación de dos factores?")) {
      return;
    }
    setSubmitting(true);
    try {
      await api<void>("/api/v1/admin/totp/disable", { method: "POST", body: { code } });
      setCode("");
      setSetupState("idle");
      setRegenerated(null);
      await loadStatus();
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setError("Código inválido.");
      } else {
        setError("No se pudo desactivar el segundo factor.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  async function regenerate(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    if (!/^\d{6}$/.test(code)) {
      setError("Ingresa el código del autenticador.");
      return;
    }
    setSubmitting(true);
    try {
      const data = await api<TotpBackupCodesResponse>(
        "/api/v1/admin/totp/backup-codes/regenerate",
        { method: "POST", body: { code } },
      );
      setRegenerated(data.backup_codes);
      setCode("");
      await loadStatus();
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setError("Código inválido.");
      } else {
        setError("No se pudieron regenerar los códigos.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  if (status === null) {
    return (
      <div className="p-6 text-sm text-muted-foreground">Cargando seguridad…</div>
    );
  }

  // Setup en curso: pasos guiados.
  if (setupState === "show-codes" && setupData) {
    return (
      <div className="p-6 max-w-2xl mx-auto space-y-6">
        <Card>
          <CardHeader>
            <h1 className="text-xl font-semibold">Activar verificación en dos pasos</h1>
            <p className="text-sm text-muted-foreground">
              Paso 1 de 2: escaneá el código QR con tu app TOTP (cualquier app de
              autenticación estándar sirve).
            </p>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex justify-center">
              <img
                src={setupData.qr_data_uri}
                alt="Código QR del segundo factor"
                className="w-48 h-48 border rounded-md bg-white p-2"
              />
            </div>
            <p className="text-sm text-muted-foreground">
              ¿No puedes escanear? La app también acepta importación manual del
              secret asociado al QR.
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <h2 className="text-lg font-semibold">Códigos de respaldo</h2>
            <p className="text-sm text-muted-foreground">
              Si pierdes tu dispositivo, usa uno de estos códigos para entrar.
              Cada uno funciona una sola vez.
            </p>
          </CardHeader>
          <CardContent>
            <BackupCodesDisplay
              codes={setupData.backup_codes}
              onAcknowledge={() => setSetupState("verify")}
            />
          </CardContent>
        </Card>
      </div>
    );
  }

  if (setupState === "verify") {
    return (
      <div className="p-6 max-w-md mx-auto">
        <Card>
          <CardHeader>
            <h1 className="text-xl font-semibold">Confirma el código</h1>
            <p className="text-sm text-muted-foreground">
              Paso 2 de 2: ingresa el código de 6 dígitos que muestra tu
              autenticador en este momento.
            </p>
          </CardHeader>
          <CardContent>
            <form className="space-y-4" onSubmit={confirmEnable} noValidate>
              <div className="space-y-2">
                <Label htmlFor="code">Código del autenticador</Label>
                <Input
                  id="code"
                  inputMode="numeric"
                  autoFocus
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  maxLength={6}
                  placeholder="123456"
                  disabled={submitting}
                />
              </div>
              {error && (
                <div
                  role="alert"
                  className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive"
                >
                  {error}
                </div>
              )}
              <Button type="submit" className="w-full" disabled={submitting}>
                {submitting ? "Verificando…" : "Activar"}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Estado normal: gestión.
  return (
    <div className="p-6 max-w-2xl mx-auto space-y-6">
      <Card>
        <CardHeader>
          <h1 className="text-xl font-semibold">Autenticación de dos factores</h1>
          <p className="text-sm text-muted-foreground">
            Agrega una capa extra de seguridad usando una app TOTP. Necesitas
            tu dispositivo cada vez que iniciás sesión.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          {status.enabled ? (
            <div className="space-y-3">
              <div className="rounded-md bg-emerald-50 border border-emerald-200 px-3 py-2 text-sm text-emerald-900 dark:bg-emerald-950/30 dark:border-emerald-900 dark:text-emerald-200">
                <strong>Activa</strong> desde {status.enabled_at?.split("T")[0] ?? "?"}.
                Códigos de respaldo restantes: {status.backup_codes_remaining}.
              </div>
              {status.last_used_at && (
                <p className="text-xs text-muted-foreground">
                  Último uso: {status.last_used_at.split("T")[0]}.
                </p>
              )}
            </div>
          ) : (
            <>
              <div className="rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-sm text-amber-900 dark:bg-amber-950/30 dark:border-amber-900 dark:text-amber-200">
                El segundo factor está <strong>desactivado</strong>.
              </div>
              <Button type="button" onClick={startSetup} disabled={submitting}>
                {submitting ? "Iniciando…" : "Activar verificación en dos pasos"}
              </Button>
            </>
          )}
          {error && (
            <div
              role="alert"
              className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive"
            >
              {error}
            </div>
          )}
          {setupState === "done" && (
            <div className="rounded-md bg-emerald-50 border border-emerald-200 px-3 py-2 text-sm text-emerald-900 dark:bg-emerald-950/30 dark:border-emerald-900 dark:text-emerald-200">
              Verificación en dos pasos activada.
            </div>
          )}
        </CardContent>
      </Card>

      {status.enabled && (
        <>
          <Card>
            <CardHeader>
              <h2 className="text-lg font-semibold">Regenerar códigos de respaldo</h2>
              <p className="text-sm text-muted-foreground">
                Invalida los códigos anteriores y genera 10 nuevos. Requiere
                código del autenticador.
              </p>
            </CardHeader>
            <CardContent>
              {regenerated ? (
                <BackupCodesDisplay
                  codes={regenerated}
                  onAcknowledge={() => setRegenerated(null)}
                />
              ) : (
                <form className="space-y-3" onSubmit={regenerate} noValidate>
                  <div className="space-y-2">
                    <Label htmlFor="regen-code">Código del autenticador</Label>
                    <Input
                      id="regen-code"
                      inputMode="numeric"
                      value={code}
                      onChange={(e) => setCode(e.target.value)}
                      maxLength={6}
                      placeholder="123456"
                      disabled={submitting}
                    />
                  </div>
                  <Button type="submit" variant="outline" disabled={submitting}>
                    {submitting ? "Regenerando…" : "Regenerar"}
                  </Button>
                </form>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <h2 className="text-lg font-semibold">Desactivar segundo factor</h2>
              <p className="text-sm text-muted-foreground">
                Removés la protección del segundo factor. Requiere código del
                autenticador. Solo hazlo si vas a re-enrolar en otro dispositivo.
              </p>
            </CardHeader>
            <CardContent>
              <form className="space-y-3" onSubmit={disable} noValidate>
                <div className="space-y-2">
                  <Label htmlFor="disable-code">Código del autenticador</Label>
                  <Input
                    id="disable-code"
                    inputMode="numeric"
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    maxLength={6}
                    placeholder="123456"
                    disabled={submitting}
                  />
                </div>
                <Button type="submit" variant="destructive" disabled={submitting}>
                  {submitting ? "Desactivando…" : "Desactivar"}
                </Button>
              </form>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
